"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import { useShell } from "@/components/shell/shell-context";
import { ApiError, api, loginUrl } from "@/lib/api/client";
import type { Briefing, Project } from "@/lib/api/types";
import { pageTypeLabel, statusLabel } from "@/lib/briefing";
import { formatCount, formatDate } from "@/lib/format";
import { PdfExportButton } from "./pdf-export-button";

type Filter = "all" | "done" | "draft";
const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "done", label: "Abgeschlossen" },
  { value: "draft", label: "Entwürfe" },
];

export function BriefingList() {
  const { project } = useShell();
  // Bei Projektwechsel neu aufbauen: Suche, Filter und Rückgängig gehören zum Projekt
  return project ? <ListView key={project.id} project={project} /> : null;
}

function ListView({ project }: { project: Project }) {
  const router = useRouter();
  const [items, setItems] = useState<Briefing[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [removed, setRemoved] = useState<Briefing | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<Briefing[]>(`/api/briefings?project_id=${project.id}`)
      .then((list) => {
        if (cancelled) return;
        setItems(list);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 401) {
          router.replace(loginUrl("/briefing"));
          return;
        }
        setError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
      });
    return () => {
      cancelled = true;
    };
  }, [project.id, attempt, router]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (items ?? []).filter(
      (b) =>
        (filter === "all" || b.status === filter) &&
        (needle === "" ||
          b.main_keyword.toLowerCase().includes(needle) ||
          (b.h1 ?? "").toLowerCase().includes(needle)),
    );
  }, [items, query, filter]);

  const remove = async (briefing: Briefing) => {
    setActionError(null);
    try {
      await api(`/api/briefings/${briefing.id}`, { method: "DELETE" });
      setItems((current) => (current ?? []).filter((b) => b.id !== briefing.id));
      setRemoved(briefing);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
    }
  };

  const undo = async () => {
    if (!removed) return;
    setActionError(null);
    try {
      await api<Briefing>(`/api/briefings/${removed.id}/restore`, { method: "POST" });
      setRemoved(null);
      setAttempt((current) => current + 1); // Reihenfolge und Stand vom Server
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
    }
  };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-7 sm:px-9">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="text-[13px] text-muted">Briefing-Erstellung · für geplante Seiten</div>
          <h1 className="text-3xl font-semibold tracking-tight">Briefings</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            href="/briefing/neu"
            className="inline-flex h-11 items-center rounded-md bg-accent px-4 text-sm font-medium text-white"
          >
            Neues Briefing
          </Link>
          <Link href="/settings/briefing" className="inline-flex min-h-11 items-center text-[13px] text-accent underline">
            Briefing-Einstellungen
          </Link>
        </div>
      </header>

      <nav aria-label="Briefing-Bereiche" className="flex flex-wrap gap-1 border-b border-line">
        <Link
          href="/briefing"
          aria-current="page"
          className="inline-flex min-h-11 items-center border-b-2 border-accent px-4 text-sm font-medium"
        >
          Vorhandene Briefings{items ? ` · ${formatCount(items.length)}` : ""}
        </Link>
        <Link href="/briefing/neu" className="inline-flex min-h-11 items-center px-4 text-sm text-muted">
          Neues Briefing
        </Link>
      </nav>

      {removed && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-md bg-ink px-4 py-2.5 text-sm text-white"
        >
          <span className="flex-1">„{removed.main_keyword}“ gelöscht</span>
          <button
            type="button"
            onClick={() => void undo()}
            className="h-11 rounded-md border border-[#5a636c] px-3 text-[13px]"
          >
            Rückgängig
          </button>
        </div>
      )}
      {actionError && (
        <p role="alert" className="text-sm text-danger">
          {actionError}
        </p>
      )}

      <section aria-labelledby="list-title" className="rounded-lg border border-line bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e6e9e7] px-5 py-3">
          <h2 id="list-title" className="text-base font-semibold">
            Vorhandene Briefings
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="briefing-search" className="sr-only">
              Briefings durchsuchen
            </label>
            <input
              id="briefing-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Keyword oder Titel suchen …"
              className="h-11 w-60 max-w-full rounded-md border border-field px-3 text-sm"
            />
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                aria-pressed={filter === f.value}
                onClick={() => setFilter(f.value)}
                className={`h-11 rounded-md border px-3 text-[13px] ${
                  filter === f.value ? "border-accent bg-accent text-white" : "border-field bg-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div role="alert" className="flex flex-wrap items-center gap-3 px-5 py-4 text-sm text-danger">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setAttempt((current) => current + 1)}
              className="h-11 rounded-md border border-danger bg-white px-3.5 text-sm"
            >
              Erneut versuchen
            </button>
          </div>
        )}
        {items === null && !error && <p className="px-5 py-6 text-sm text-muted">Briefings werden geladen …</p>}

        {items !== null && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted">
                    <th className="px-5 py-2.5 font-medium">Briefing</th>
                    <th className="px-3 py-2.5 font-medium">Seitentyp</th>
                    <th className="px-3 py-2.5 font-medium">Status</th>
                    <th className="px-3 py-2.5 text-right font-medium">Keywords · Prompts · Fan-outs</th>
                    <th className="px-3 py-2.5 font-medium">Zuletzt geändert</th>
                    <th className="px-5 py-2.5 text-right font-medium">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((b) => (
                    <tr key={b.id} className="border-t border-neutral-bg">
                      <td className="px-5 py-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium">{b.main_keyword}</span>
                          <span className="text-xs text-muted">{b.h1 ?? "noch kein Titel"}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">{pageTypeLabel(b.page_type)}</td>
                      <td className="px-3 py-3">
                        <span
                          className={`whitespace-nowrap rounded px-2 py-[3px] text-xs font-medium ${
                            b.status === "done" ? "bg-success-bg text-success" : "bg-warning-bg text-warning"
                          }`}
                        >
                          {statusLabel(b)}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-[13px]">
                        {formatCount(b.keyword_count)} · {formatCount(b.prompt_count)} ·{" "}
                        {formatCount(b.fanout_count)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-[#3a424a]">
                        {formatDate(b.updated_at)}
                        {b.updated_by ? ` · ${b.updated_by}` : ""}
                      </td>
                      <td className="whitespace-nowrap py-1.5 pl-3 pr-4 text-right">
                        <Link
                          href={`/briefing/${b.id}`}
                          aria-label={`${b.status === "done" ? "Öffnen" : "Fortsetzen"}: ${b.main_keyword}`}
                          className="inline-flex h-11 items-center rounded-md border border-field bg-white px-3 text-[13px]"
                        >
                          {b.status === "done" ? "Öffnen" : "Fortsetzen"}
                        </Link>
                        {b.status === "done" && (
                          <span className="ml-1 align-middle">
                            <PdfExportButton briefingId={b.id} mainKeyword={b.main_keyword} compact />
                            <Link
                              href={`/onpage/neu?briefing=${b.id}`}
                              aria-label={`Artikel prüfen: ${b.main_keyword}`}
                              title="Den veröffentlichten Artikel in der Onpage-Optimierung gegen dieses Briefing prüfen"
                              className="ml-1 inline-flex h-11 items-center rounded-md border border-field bg-white px-3 text-[13px]"
                            >
                              Artikel prüfen
                            </Link>
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => void remove(b)}
                          aria-label={`Briefing „${b.main_keyword}“ löschen`}
                          className="ml-1 inline-flex size-11 items-center justify-center rounded-md align-middle text-danger"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {visible.length === 0 && (
              <p className="px-5 py-6 text-sm text-muted">
                {items.length === 0
                  ? "Noch keine Briefings. Mit „Neues Briefing“ geht es los."
                  : "Keine Briefings gefunden."}
              </p>
            )}
            <p className="border-t border-neutral-bg px-5 py-3 text-xs text-muted">
              Entwürfe werden nach jedem Schritt automatisch gespeichert und öffnen sich beim letzten bearbeiteten
              Schritt.
            </p>
          </>
        )}
      </section>
    </div>
  );
}
